import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Flame, Loader2, RefreshCw, LogOut, Phone, Trash2, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "etm_admin_key";

export default function AdminPage() {
  const [key, setKey] = useState(sessionStorage.getItem(STORAGE_KEY) || "");
  const [keyInput, setKeyInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState(null);
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [gFile, setGFile] = useState(null);
  const [gTitle, setGTitle] = useState("");
  const [gLocation, setGLocation] = useState("");
  const [gTag, setGTag] = useState("After");
  const [gUploading, setGUploading] = useState(false);

  const fetchLeads = useCallback(async (k) => {
    const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Key": k } });
    setLeads(res.data);
  }, []);

  const fetchGallery = useCallback(async () => {
    const res = await axios.get(`${API}/gallery`);
    setGallery(res.data);
  }, []);

  useEffect(() => {
    if (!key) return;
    setLoading(true);
    fetchLeads(key)
      .then(() => {
        setAuthed(true);
        fetchGallery();
      })
      .catch(() => {
        sessionStorage.removeItem(STORAGE_KEY);
        setKey("");
      })
      .finally(() => setLoading(false));
  }, [key, fetchLeads, fetchGallery]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { key: keyInput });
      sessionStorage.setItem(STORAGE_KEY, keyInput);
      setKey(keyInput);
    } catch (err) {
      setAuthError("Invalid admin key. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setKey("");
    setAuthed(false);
    setLeads(null);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await fetchLeads(key);
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    e.preventDefault();
    if (!gFile) return;
    setGUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", gFile);
      fd.append("title", gTitle || "Project");
      fd.append("location", gLocation);
      fd.append("tag", gTag);
      await axios.post(`${API}/gallery`, fd, { headers: { "X-Admin-Key": key } });
      setGFile(null);
      setGTitle("");
      setGLocation("");
      setGTag("After");
      await fetchGallery();
    } finally {
      setGUploading(false);
    }
  };

  const handleGalleryDelete = async (id) => {
    await axios.delete(`${API}/gallery/${id}`, { headers: { "X-Admin-Key": key } });
    await fetchGallery();
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-muted flex items-center justify-center px-4">
        <div className="bg-card border border-border rounded-md p-8 w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <span className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" strokeWidth={2} />
            </span>
            <span className="font-heading font-bold text-lg tracking-tight">Lead Dashboard</span>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-key">Admin Key</Label>
              <Input
                id="admin-key"
                data-testid="admin-key-input"
                type="password"
                placeholder="Enter admin key"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
              />
            </div>
            {authError && (
              <p data-testid="admin-login-error" className="text-sm text-destructive">{authError}</p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="btn-press w-full bg-primary hover:bg-[#A93A24] text-white font-bold h-11 rounded-md"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <a href="/" data-testid="admin-back-home-link" className="block text-center text-sm text-muted-foreground hover:text-foreground mt-6 transition-colors">
            Back to website
          </a>
        </div>
      </div>
    );
  }

  const today = new Date().toDateString();
  const stats = [
    { label: "Total Leads", value: leads?.length ?? 0, testid: "stat-total" },
    { label: "$249 Bookings", value: leads?.filter((l) => l.source === "book_now").length ?? 0, testid: "stat-bookings" },
    { label: "Quote Requests", value: leads?.filter((l) => l.source === "quote_request").length ?? 0, testid: "stat-quotes" },
    { label: "Today", value: leads?.filter((l) => new Date(l.created_at).toDateString() === today).length ?? 0, testid: "stat-today" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-muted">
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <Flame className="w-4 h-4 text-white" strokeWidth={2} />
            </span>
            <span className="font-heading font-bold tracking-tight">Exotictileandmasonry — Leads</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={loading}
              data-testid="admin-refresh-button"
              className="btn-press"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="btn-press"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-border rounded-md p-4" data-testid={s.testid}>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{s.label}</p>
              <p className="font-heading text-3xl font-bold text-foreground mt-2">{s.value}</p>
            </div>
          ))}
        </div>
        <div className="bg-white border border-border rounded-md overflow-x-auto" data-testid="admin-leads-table-wrapper">
          <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Preferred Date</TableHead>
                <TableHead>Photo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(leads || []).map((lead) => (
                <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {new Date(lead.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-semibold">{lead.name}</TableCell>
                  <TableCell>
                    <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 text-primary font-semibold hover:underline">
                      <Phone className="w-3 h-3" /> {lead.phone}
                    </a>
                  </TableCell>
                  <TableCell className="text-sm">{lead.service}</TableCell>
                  <TableCell>
                    <Badge
                      data-testid={`lead-type-${lead.id}`}
                      className={lead.source === "book_now" ? "bg-primary text-white" : "bg-accent text-white"}
                    >
                      {lead.source === "book_now" ? "$249 Booking" : "Quote"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{lead.preferred_date || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {lead.attachment_path ? (
                      <a
                        href={`${API}/files/${lead.attachment_path}?key=${key}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`lead-photo-${lead.id}`}
                        className="text-primary font-semibold hover:underline"
                      >
                        View
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {(leads || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-12" data-testid="leads-empty-state">
                    No leads yet. They'll appear here as soon as someone books or requests a quote.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-10" data-testid="gallery-manager">
          <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Gallery Photos</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Upload real before/after job photos. Once you add at least one, your photos replace the sample images on the website.
          </p>
          <form
            onSubmit={handleGalleryUpload}
            data-testid="gallery-upload-form"
            className="bg-white border border-border rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end"
          >
            <div className="space-y-2">
              <Label htmlFor="g-file">Photo</Label>
              <Input id="g-file" data-testid="gallery-file-input" type="file" accept="image/*" onChange={(e) => setGFile(e.target.files?.[0] || null)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-title">Title</Label>
              <Input id="g-title" data-testid="gallery-title-input" placeholder="Crown Rebuild" value={gTitle} onChange={(e) => setGTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-location">Location</Label>
              <Input id="g-location" data-testid="gallery-location-input" placeholder="Portland" value={gLocation} onChange={(e) => setGLocation(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-tag">Tag</Label>
              <select
                id="g-tag"
                data-testid="gallery-tag-select"
                value={gTag}
                onChange={(e) => setGTag(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              >
                <option value="After">After</option>
                <option value="Before">Before</option>
              </select>
            </div>
            <Button
              type="submit"
              disabled={gUploading || !gFile}
              data-testid="gallery-upload-button"
              className="btn-press bg-primary hover:bg-[#A93A24] text-white font-bold rounded-md"
            >
              {gUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span className="inline-flex items-center gap-2">
                  <ImagePlus className="w-4 h-4" /> Upload
                </span>
              )}
            </Button>
          </form>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="admin-gallery-grid">
            {gallery.map((p) => (
              <div key={p.id} data-testid={`admin-gallery-item-${p.id}`} className="border border-border rounded-md overflow-hidden bg-white">
                <img src={`${API}/files/${p.storage_path}`} alt={p.title} className="w-full aspect-[4/5] object-cover" />
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {p.tag}{p.location ? ` · ${p.location}` : ""}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleGalleryDelete(p.id)}
                    data-testid={`gallery-delete-${p.id}`}
                    className="btn-press shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {gallery.length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full py-6" data-testid="gallery-empty-state">
                No photos uploaded yet — the website is showing sample images.
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
